import React, { useCallback, useContext, useEffect, useState } from "react";
import { Building2, Globe } from "lucide-react";
import CfLoader from "../../CfLoader/CfLoader";
import Toast from "../../Toast/Toast";
import EntityConstants from "../../../configs/EntityConstants";
import { GET } from "../../../service/CRUDService";
import AuthContext from "../../../store/auth-context";
import { CARD_CLASS } from "../utils/styles";

const TENANT_PAGE_SIZE = 6;
const ORG_PAGE_SIZE = 10;

function normalizePaginatedResponse(data) {
  const results = Array.isArray(data?.results) ? data.results : [];
  return {
    results,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

function ScopeOptionCard({
  id,
  selected,
  onSelect,
  icon: Icon,
  title,
  description,
  disabled = false,
}) {
  return (
    <button
      type="button"
      id={id}
      onClick={onSelect}
      disabled={disabled}
      className={`w-full text-left p-4 sm:p-5 rounded-xl border-2 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary disabled:cursor-not-allowed disabled:opacity-60 ${
        selected
          ? "border-cfPrimary bg-cfPrimaryMuted shadow-sm"
          : "border-cfCardBorder bg-cfSecondary hover:border-cfPrimary/40 hover:bg-cfCardHover"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            selected
              ? "bg-cfPrimaryMuted"
              : "bg-cfSecondary border border-cfCardBorder"
          }`}
        >
          <Icon
            className={`w-5 h-5 ${selected ? "text-cfPrimary" : "text-cfGlobalText"}`}
            aria-hidden
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-cfHeadingText">{title}</p>
          {description && (
            <p className="text-xs text-cfGlobalText mt-1 leading-relaxed">
              {description}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function ListRow({ id, selected, onSelect, primary, secondary, badge, disabled = false }) {
  return (
    <button
      type="button"
      id={id}
      onClick={onSelect}
      disabled={disabled}
      className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 border-b border-cfCardBorder last:border-b-0 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cfPrimary disabled:cursor-not-allowed disabled:opacity-60 ${
        selected ? "bg-cfPrimaryMuted" : "bg-cfSecondary hover:bg-cfCardHover"
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-cfHeadingText truncate">
          {primary}
        </p>
        <p className="text-xs text-cfGlobalText font-mono mt-0.5 truncate">
          {secondary}
        </p>
      </div>
      {badge}
    </button>
  );
}

function CertificateOnboardingScopePicker({ t, value, onChange, disabled = false }) {
  const authContext = useContext(AuthContext);
  const isSuperAdmin = authContext.userType?.toLowerCase() === "super_admin";
  const tenantCode =
    authContext.tenantCode || localStorage.getItem("tenantCode") || "";

  const [superAdminMode, setSuperAdminMode] = useState(() => {
    if (value?.type === "general") return "general";
    if (value?.type === "tenant") return "tenant";
    return null;
  });

  const [tenants, setTenants] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantOffset, setTenantOffset] = useState(0);
  const [tenantNext, setTenantNext] = useState(null);
  const [tenantPrevious, setTenantPrevious] = useState(null);

  const [organisations, setOrganisations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgSearch, setOrgSearch] = useState("");

  const fetchTenants = useCallback(
    async (offset = 0, search = "") => {
      setTenantLoading(true);
      try {
        const params = new URLSearchParams({
          limit: String(TENANT_PAGE_SIZE),
          offset: String(offset),
          is_active: "true",
        });
        if (search) {
          params.set("search", search);
        }
        const endpoint = `${EntityConstants.GET_TENANT_DATA}?${params.toString()}`;
        const response = await GET(endpoint);
        const data = await response.json();
        const resData = data.data;

        if (resData?.error) {
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message: resData.error,
          });
          setTenants([]);
          setTenantNext(null);
          setTenantPrevious(null);
        } else {
          const tenantData = normalizePaginatedResponse(resData);
          setTenants(tenantData.results);
          setTenantNext(tenantData.next);
          setTenantPrevious(tenantData.previous);
          setTenantOffset(offset);
        }
      } catch (error) {
        console.error("Failed to load tenants", error);
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: t("tenant:failed_load_tenants"),
        });
        setTenants([]);
        setTenantNext(null);
        setTenantPrevious(null);
        setTenantOffset(0);
      } finally {
        setTenantLoading(false);
      }
    },
    [t],
  );

  const fetchOrganisations = useCallback(
    async (search = "") => {
      if (!tenantCode) return;
      setOrgLoading(true);
      try {
        const params = new URLSearchParams({
          tenant_code: tenantCode,
          limit: String(ORG_PAGE_SIZE),
          offset: "0",
        });
        if (search) {
          params.set("search", search);
        }
        const response = await GET(
          `${EntityConstants.GET_ORGANISATIONS}?${params.toString()}`,
        );
        const data = await response.json();

        if (data.success) {
          const orgData = normalizePaginatedResponse(data.data);
          const activeOrgs = orgData.results.filter(
            (org) => org.is_active,
          );
          setOrganisations(activeOrgs);
        } else {
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message: data.message || t("tenant:failed_fetch_organisations"),
          });
          setOrganisations([]);
        }
      } catch (error) {
        console.error("Failed to load organisations", error);
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: t("tenant:failed_fetch_organisations"),
        });
        setOrganisations([]);
      } finally {
        setOrgLoading(false);
      }
    },
    [tenantCode, t],
  );

  useEffect(() => {
    if (isSuperAdmin && superAdminMode === "tenant") {
      fetchTenants(0, tenantSearch);
    }
  }, [isSuperAdmin, superAdminMode, fetchTenants]);

  useEffect(() => {
    if (!isSuperAdmin) {
      fetchOrganisations(orgSearch);
    }
  }, [isSuperAdmin, fetchOrganisations]);

  const handleTenantSearch = (event) => {
    const nextSearch = event.target.value;
    setTenantSearch(nextSearch);
    fetchTenants(0, nextSearch);
  };

  const handleOrgSearch = (event) => {
    const nextSearch = event.target.value;
    setOrgSearch(nextSearch);
    fetchOrganisations(nextSearch);
  };

  const handleSelectGeneral = () => {
    setSuperAdminMode("general");
    onChange({
      type: "general",
      label: t("certificateOnboarding:complete_scope_general"),
    });
  };

  const handleSelectTenantMode = () => {
    setSuperAdminMode("tenant");
    onChange(null);
  };

  const handleSelectTenant = (tenant) => {
    onChange({
      type: "tenant",
      code: tenant.code,
      label: tenant.name,
    });
  };

  const handleSelectOrganisation = (org) => {
    onChange({
      type: "organisation",
      code: org.org_code,
      label: org.org_name,
    });
  };

  const activeBadge = (isActive) => (
    <span
      className={`shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive
          ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG text-cfIssuedCredentialTemplateCardStatusText"
          : "bg-cfIssuedCredentialTemplateCardStatusRevokedBG text-white"
      }`}
    >
      {isActive ? t("tenant:active") : t("tenant:inactive")}
    </span>
  );

  return (
    <div id="cert-onboarding-scope-picker" className="space-y-4">
      {isSuperAdmin ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ScopeOptionCard
              id="cert-onboarding-scope-tenant-mode"
              selected={superAdminMode === "tenant"}
              onSelect={handleSelectTenantMode}
              icon={Building2}
              title={t("certificateOnboarding:scope_tenant_option")}
              description={t("certificateOnboarding:scope_select_tenant")}
              disabled={disabled}
            />
            <ScopeOptionCard
              id="cert-onboarding-scope-general"
              selected={superAdminMode === "general"}
              onSelect={handleSelectGeneral}
              icon={Globe}
              title={t("certificateOnboarding:scope_general_option")}
              description={t("certificateOnboarding:scope_general_description")}
              disabled={disabled}
            />
          </div>

          {superAdminMode === "tenant" && (
            <div className={`${CARD_CLASS} overflow-hidden`}>
              <div className="p-4 border-b border-cfCardBorder">
                <div className="relative">
                  <input
                    type="search"
                    id="cert-onboarding-scope-tenant-search"
                    value={tenantSearch}
                    onChange={handleTenantSearch}
                    disabled={disabled}
                    placeholder={t("tenant:search_tenants_name_code")}
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-cfCardBorder rounded-lg bg-cfSecondary text-cfHeadingText placeholder:text-cfGlobalText focus:outline-none focus:ring-2 focus:ring-cfPrimary/20 focus:border-cfPrimary"
                  />
                </div>
              </div>

              {tenantLoading ? (
                <div className="flex justify-center py-12">
                  <CfLoader />
                </div>
              ) : tenants.length === 0 ? (
                <p className="text-sm text-cfGlobalText text-center py-10 px-4">
                  {t("tenant:no_tenants_found")}
                </p>
              ) : (
                <div>
                  {tenants.map((tenant) => (
                    <ListRow
                      key={tenant.code}
                      id={`cert-onboarding-scope-tenant-${tenant.code}`}
                      selected={value?.code === tenant.code}
                      onSelect={() => handleSelectTenant(tenant)}
                      primary={tenant.name}
                      secondary={tenant.code}
                      badge={activeBadge(tenant.is_active)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              )}

              {(tenantPrevious !== null || tenantNext !== null) && (
                <div className="flex justify-between items-center p-4 border-t border-cfCardBorder">
                  <button
                    type="button"
                    onClick={() =>
                      fetchTenants(
                        Math.max(0, tenantOffset - TENANT_PAGE_SIZE),
                        tenantSearch,
                      )
                    }
                    disabled={tenantPrevious === null || tenantLoading || disabled}
                    className="px-4 py-2 text-sm border border-cfCardBorder rounded-md text-cfHeadingText hover:bg-cfCardHover disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("certificateOnboarding:back")}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      fetchTenants(
                        tenantOffset + TENANT_PAGE_SIZE,
                        tenantSearch,
                      )
                    }
                    disabled={tenantNext === null || tenantLoading || disabled}
                    className="px-4 py-2 text-sm border border-cfCardBorder rounded-md text-cfHeadingText hover:bg-cfCardHover disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("certificateOnboarding:scope_next")}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className={`${CARD_CLASS} overflow-hidden`}>
          <div className="p-4 border-b border-cfCardBorder">
            <div className="relative">
              <input
                type="search"
                id="cert-onboarding-scope-org-search"
                value={orgSearch}
                onChange={handleOrgSearch}
                disabled={disabled}
                placeholder={t("tenant:search_organisations_name_code")}
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-cfCardBorder rounded-lg bg-cfSecondary text-cfHeadingText placeholder:text-cfGlobalText focus:outline-none focus:ring-2 focus:ring-cfPrimary/20 focus:border-cfPrimary"
              />
            </div>
          </div>

          {orgLoading ? (
            <div className="flex justify-center py-12">
              <CfLoader />
            </div>
          ) : organisations.length === 0 ? (
            <p className="text-sm text-cfGlobalText text-center py-10 px-4">
              {t("tenant:no_organisations_found")}
            </p>
          ) : (
            <div>
              {organisations.map((org) => (
                <ListRow
                  key={org.org_code}
                  id={`cert-onboarding-scope-org-${org.org_code}`}
                  selected={value?.code === org.org_code}
                  onSelect={() => handleSelectOrganisation(org)}
                  primary={org.org_name}
                  secondary={org.org_code}
                  badge={activeBadge(org.is_active)}
                  disabled={disabled}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CertificateOnboardingScopePicker;
